import { useSync } from '../context/SyncContext';
import { Button } from '../components/ui/button';
import SyncBadge from '../components/SyncBadge';
import { POSITIONS, ACTION_LABELS } from '../lib/status';
import { RefreshCw, RotateCcw, CloudUpload, CheckCircle2, WifiOff } from 'lucide-react';

export default function SyncQueue() {
  const { queue, online, pendingCount, failedCount, processQueue, retryItem } = useSync();

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><p className="overline text-primary">Offline Engine</p><h1 className="font-head text-3xl font-bold">Sync Queue</h1></div>
        <Button data-testid="sync-all-btn" onClick={() => processQueue()} disabled={!online || pendingCount === 0}>
          <RefreshCw className="mr-1.5 h-4 w-4" /> Sync All
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-border bg-card p-4"><p className="overline text-muted-foreground">Pending</p><p className="font-head text-3xl font-extrabold text-sky-400">{pendingCount}</p></div>
        <div className="rounded-xl border border-border bg-card p-4"><p className="overline text-muted-foreground">Failed</p><p className="font-head text-3xl font-extrabold text-rose-400">{failedCount}</p></div>
        <div className="rounded-xl border border-border bg-card p-4"><p className="overline text-muted-foreground">Network</p><p className={`font-head text-3xl font-extrabold ${online ? 'text-emerald-400' : 'text-amber-400'}`}>{online ? 'ONLINE' : 'OFFLINE'}</p></div>
      </div>

      {queue.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-12 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-400" />
          <p className="font-head text-lg font-bold">All caught up</p>
          <p className="text-sm text-muted-foreground">No actions waiting to sync. Captured proofs upload automatically when online.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {queue.map((item) => (
            <div key={item.id} data-testid={`queue-item-${item.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary">
                {online ? <CloudUpload className="h-5 w-5 text-primary" /> : <WifiOff className="h-5 w-5 text-amber-400" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{ACTION_LABELS[item.payload.action_type] || item.payload.action_type}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {item.files?.length || 0} media · {item.payload.gps ? `GPS ${item.payload.gps.lat?.toFixed(4)},${item.payload.gps.lng?.toFixed(4)}` : 'no GPS'} · {item.createdAt.slice(11, 19)}
                </p>
                {item.error && <p className="truncate text-xs text-rose-400">{typeof item.error === 'string' ? item.error : 'Sync error'}</p>}
              </div>
              <SyncBadge status={item.status} />
              {item.status === 'failed' && (
                <Button size="sm" variant="secondary" data-testid={`retry-${item.id}`} onClick={() => retryItem(item.id)}>
                  <RotateCcw className="mr-1 h-3.5 w-3.5" /> Retry
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
